import { NIVEIS_DE_APLICACAO, type NivelDeAplicacao } from '../../types/nivelDeAplicacao';

/**
 * O endereço da tela de aplicação (#606, D14). Quem chega por um link — lista de Cargos,
 * Setores, Unidades, ficha do cargo, ficha do risco, pendência da emissão — cai no destino que
 * o link pediu, em vez de numa tela em branco.
 *
 * Os dois formatos antigos continuam valendo sem mudança: `nivel=unit&companyId=&unitId=`
 * (lista de Unidades) e só `companyId` (links da emissão), que abre o "Para quem?" com a
 * empresa escolhida e a busca de destino restrita a ela. `aba`: `adicionar` abre o painel de
 * incluir; `aplicados` e `herdados` só posicionam a tabela (o segundo, no que vem de cima).
 */
export const ABAS_DO_ASSISTENTE = ['adicionar', 'aplicados', 'herdados'] as const;

export type AbaDoAssistente = (typeof ABAS_DO_ASSISTENTE)[number];

export interface EnderecoDoAssistente {
  nivel: NivelDeAplicacao | null;
  companyId: number | null;
  unitId: number | null;
  departmentId: number | null;
  occupationId: number | null;
  employeeId: number | null;
  /** Ausente, a tela só mostra o inventário; `adicionar` abre o painel de incluir. */
  aba: AbaDoAssistente | null;
  /** Id do item a destacar no painel. */
  item: number | null;
  /** Para onde "Concluir" leva: o caminho interno de quem abriu o assistente. */
  voltar: string | null;
}

type CampoDeId = 'companyId' | 'unitId' | 'departmentId' | 'occupationId' | 'employeeId';

/** Cada nível é identificado pelo seu próprio parâmetro. */
export const CAMPO_DO_NIVEL: Record<NivelDeAplicacao, CampoDeId> = {
  company: 'companyId',
  unit: 'unitId',
  department: 'departmentId',
  occupation: 'occupationId',
  employee: 'employeeId',
};

/** O que um link pede ao assistente; os ids vêm pelo nível, convertidos para texto aqui. */
export interface PedidoDeEndereco {
  nivel?: NivelDeAplicacao;
  ids?: Partial<Record<NivelDeAplicacao, number | string | null>>;
  aba?: AbaDoAssistente;
  item?: number | string;
  /** O caminho interno de onde a pessoa veio; "Concluir" volta para ele. */
  voltar?: string;
}

/**
 * O único montador do endereço D14, par de `lerEndereco`: nível, ids da empresa para baixo, aba,
 * item e o caminho de volta, nessa ordem. Id ausente ou nulo fica de fora — o destino incompleto
 * fica por completar no "Para quem?".
 */
export function montarEndereco(caminho: string, { nivel, ids = {}, aba, item, voltar }: PedidoDeEndereco): string {
  const busca = new URLSearchParams();
  if (nivel) busca.set('nivel', nivel);
  for (const degrau of NIVEIS_DE_APLICACAO) {
    const id = ids[degrau];
    if (id !== undefined && id !== null) busca.set(CAMPO_DO_NIVEL[degrau], String(id));
  }
  if (aba) busca.set('aba', aba);
  if (item !== undefined) busca.set('item', String(item));
  const volta = caminhoInterno(voltar ?? null);
  if (volta) busca.set('voltar', volta);
  const texto = busca.toString();
  return texto ? `${caminho}?${texto}` : caminho;
}

export function lerEndereco(busca: string): EnderecoDoAssistente {
  const parametros = new URLSearchParams(busca);
  const aba = parametros.get('aba');
  return {
    nivel: NIVEIS_DE_APLICACAO.find((nivel) => nivel === parametros.get('nivel')) ?? null,
    companyId: idValido(parametros.get('companyId')),
    unitId: idValido(parametros.get('unitId')),
    departmentId: idValido(parametros.get('departmentId')),
    occupationId: idValido(parametros.get('occupationId')),
    employeeId: idValido(parametros.get('employeeId')),
    aba: ABAS_DO_ASSISTENTE.find((valida) => valida === aba) ?? null,
    item: idValido(parametros.get('item')),
    voltar: caminhoInterno(parametros.get('voltar')),
  };
}

/** O destino que o endereço pede, quando está completo; null deixa o "Para quem?" por completar. */
export function destinoDoEndereco(
  endereco: EnderecoDoAssistente
): { nivel: NivelDeAplicacao; id: number } | null {
  if (!endereco.nivel) return null;
  const id = endereco[CAMPO_DO_NIVEL[endereco.nivel]];
  return id === null ? null : { nivel: endereco.nivel, id };
}

/** Um destino por vez: link antigo com vários ids ("5,6") abre o primeiro, em vez de nenhum. */
function idValido(texto: string | null): number | null {
  if (texto === null) return null;
  const [primeiro] = texto.split(',');
  const valor = Number(primeiro);
  return primeiro.trim() !== '' && Number.isInteger(valor) && valor > 0 ? valor : null;
}

/** Tab, quebra de linha e os demais caracteres de controle (U+0000 a U+001F e U+007F). */
function temCaractereDeControle(texto: string): boolean {
  return Array.from(texto).some((letra) => {
    const codigo = letra.charCodeAt(0);
    return codigo <= 0x1f || codigo === 0x7f;
  });
}

/**
 * O caminho de volta só vale dentro do sistema. Quem decide é a resolução real do endereço, não o
 * texto: o navegador descarta tab e quebra de linha e lê a barra invertida como `/`, então
 * `/<tab>/site` vira `//site`, outro site. Aceita só o que começa com uma barra, não tem caractere de controle e,
 * resolvido, continua nesta origem; devolve o caminho já normalizado. Qualquer outra coisa vira null.
 */
export function caminhoInterno(texto: string | null): string | null {
  if (!texto || !texto.startsWith('/') || temCaractereDeControle(texto)) return null;
  const { origin } = window.location;
  let resolvido: URL;
  try {
    resolvido = new URL(texto, origin);
  } catch {
    return null;
  }
  if (resolvido.origin !== origin) return null;
  const caminho = `${resolvido.pathname}${resolvido.search}${resolvido.hash}`;
  return caminho.startsWith('//') ? null : caminho;
}
